import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClipboardListIcon,
  CubeIcon,
  UsersIcon,
  XIcon,
  UserAddIcon,
} from "@heroicons/react/outline";
import { projectsAPI } from "../api/projects";
import { stagesAPI } from "../api/stages";
import LoadingSpinner from "../components/common/LoadingSpinner";
import toast from "react-hot-toast";
import StageForm from '../components/stages/StageForm';
import TaskForm from '../components/tasks/TaskForm';
import ProjectForm from "../components/projects/ProjectForm";
import { laborAPI } from '../api/labor';
import { workersAPI } from '../api/workers';
import AssignUserModal from "../components/projects/AssignUserModal";

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const queryClient = useQueryClient();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedWorkerForAssign, setSelectedWorkerForAssign] = useState(null);

  // Fetch project details
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: () => projectsAPI.getById(id),
  });

  // Fetch project stages
  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ["project-stages", id],
    queryFn: () => projectsAPI.getStages(id),
  });

  // Fetch project tasks
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ["project-tasks", id],
    queryFn: () => projectsAPI.getTasks(id),
  });

  const { data: workersData } = useQuery({
    queryKey: ["workers"],
    queryFn: () => workersAPI.getAll(),
  });

  const { data: laborSummaryData, isLoading: laborSummaryLoading, refetch: refetchLaborSummary } = useQuery({
  queryKey: ['labor-summary', id],
  queryFn: () => laborAPI.getSummary(id),
  enabled: !!id,
});

// Fetch assignments for this project
const { data: assignmentsData, isLoading: assignmentsLoading, refetch: refetchAssignments } = useQuery({
  queryKey: ['project-assignments', id],
  queryFn: () => laborAPI.getAssignments({ project_id: id }),
  enabled: !!id,
});

  const { data: timelineData, isLoading: timelineLoading } = useQuery({
  queryKey: ['project-timeline', id],
  queryFn: () => projectsAPI.getTimeline(id),
  enabled: activeTab === 'timeline',
});

const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
  queryKey: ['project-dashboard', id],
  queryFn: () => projectsAPI.getDashboard(id),
  enabled: activeTab === 'dashboard',
});

// Add assignment mutation
const assignMutation = useMutation({
  mutationFn: (data) => laborAPI.assignWorker(data),
  onSuccess: () => {
    toast.success("Worker assigned successfully");
    setShowAssignModal(false);
    // Refetch both summaries and assignments
    refetchLaborSummary();
    refetchAssignments();
    queryClient.invalidateQueries(["labor-summary", id]);
    queryClient.invalidateQueries(["project-assignments", id]);
  },
  onError: (error) => {
    toast.error(
      error.response?.data?.error?.message || "Failed to assign worker",
    );
  },
});

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => projectsAPI.delete(id),
    onSuccess: () => {
      toast.success("Project deleted successfully");
      navigate("/projects");
    },
    onError: () => toast.error("Failed to delete project"),
  });

  if (projectLoading) return <LoadingSpinner />;

  const project = projectData?.data;
  const stages = stagesData?.data || [];
  const tasks = tasksData?.data || [];

  const getStatusColor = (status) => {
    const colors = {
      active: "badge-success",
      planning: "badge-info",
      on_hold: "badge-warning",
      completed: "badge-primary",
      cancelled: "badge-danger",
      pending: "badge-info",
      in_progress: "badge-warning",
      blocked: "badge-danger",
      done: "badge-success",
    };
    return colors[status] || "badge-info";
  };

 const tabs = [
   { id: "overview", label: "Overview", icon: ChartBarIcon },
   { id: "stages", label: "Stages", icon: ClipboardListIcon },
   { id: "tasks", label: "Tasks", icon: CubeIcon },
   { id: "labor", label: "Labor", icon: UsersIcon },
   { id: "budget", label: "Budget", icon: CurrencyDollarIcon },
   { id: "timeline", label: "Timeline", icon: CalendarIcon }, // ← NEW
   { id: "dashboard", label: "Dashboard", icon: ChartBarIcon }, // ← NEW
 ];

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      deleteMutation.mutate();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/projects"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {project?.name}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {project?.description || "No description"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAssignModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <UserAddIcon className="h-4 w-4" />
            Assign Users
          </button>
          <button
            onClick={() => setShowEditModal(true)} // ← Change this
            className="btn-secondary flex items-center gap-2"
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Project Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-slate-500 dark:text-slate-400">Status</p>
          <span
            className={`badge ${getStatusColor(project?.status)} text-sm mt-1`}
          >
            {project?.status || "Planning"}
          </span>
        </div>
        <div className="card">
          <p className="text-sm text-slate-500 dark:text-slate-400">Budget</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white">
            ${project?.total_budget?.toLocaleString() || "0"}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-500 dark:text-slate-400">Spent</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white">
            ${project?.current_spent?.toLocaleString() || "0"}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-500 dark:text-slate-400">Progress</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white">
            {project?.current_spent && project?.total_budget
              ? Math.round((project.current_spent / project.total_budget) * 100)
              : 0}
            %
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {dashboardLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Total Workers
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {dashboardData?.data?.workerSummary?.total_workers || 0}
                  </p>
                </div>
                <div className="card">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Active Workers
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {dashboardData?.data?.workerSummary?.active_workers || 0}
                  </p>
                </div>
                <div className="card">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Total Materials
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {dashboardData?.data?.stockSummary?.total_materials || 0}
                  </p>
                </div>
                <div className="card">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Low Stock Items
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {dashboardData?.data?.stockSummary?.low_stock_items || 0}
                  </p>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="card">
                <h4 className="font-medium text-slate-900 dark:text-white mb-4">
                  Recent Activity
                </h4>
                <div className="space-y-2">
                  {(dashboardData?.data?.recentActivity || []).map(
                    (activity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/30"
                      >
                        <div className="w-8 h-8 bg-primary-50 dark:bg-primary-900/20 rounded-full flex items-center justify-center text-primary-500">
                          {activity.action?.charAt(0).toUpperCase() || "📋"}
                        </div>
                        <div>
                          <p className="text-sm text-slate-900 dark:text-white">
                            {activity.action}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {activity.created_at
                              ? new Date(activity.created_at).toLocaleString()
                              : "Just now"}
                          </p>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="mt-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Project Details
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Client: {project?.client_name || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Start:{" "}
                      {project?.start_date
                        ? new Date(project.start_date).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      End:{" "}
                      {project?.expected_end_date
                        ? new Date(
                            project.expected_end_date,
                          ).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CurrencyDollarIcon className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Priority: {project?.priority || "Medium"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="card">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Quick Stats
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Stages
                    </p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {stages.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Tasks
                    </p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {tasks.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "stages" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Project Stages
              </h3>
              <button
                onClick={() => setShowStageModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add Stage
              </button>
            </div>
            {stages.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardListIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">
                  No stages yet
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {stages.map((stage) => (
                  <div key={stage.id} className="card">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                          {stage.stage_name}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {stage.description}
                        </p>
                      </div>
                      <span className={`badge ${getStatusColor(stage.status)}`}>
                        {stage.status || "Pending"}
                      </span>
                    </div>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Progress
                        </span>
                        <span className="text-slate-900 dark:text-white">
                          {stage.completion_percentage || 0}%
                        </span>
                      </div>
                      <div className="mt-1 w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                          style={{
                            width: `${stage.completion_percentage || 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "tasks" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Project Tasks
              </h3>
              <button
                onClick={() => setShowTaskModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add Task
              </button>
            </div>
            {tasks.length === 0 ? (
              <div className="text-center py-12">
                <CubeIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">
                  No tasks yet
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div key={task.id} className="card">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                          {task.title}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                          {task.description}
                        </p>
                      </div>
                      <span className={`badge ${getStatusColor(task.status)}`}>
                        {task.status || "Todo"}
                      </span>
                    </div>
                    {task.assigned_to_name && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <UserIcon className="h-4 w-4" />
                        <span>Assigned to: {task.assigned_to_name}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'labor' && (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Project Assignments</h3>
      <button 
        onClick={() => setShowAssignModal(true)}
        className="btn-primary flex items-center gap-2"
      >
        <UserAddIcon className="h-4 w-4" />
        Assign User to Project
      </button>
    </div>

    {/* Labor Summary Cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="card">
        <p className="text-sm text-slate-500 dark:text-slate-400">Total Assigned</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">
          {assignmentsData?.data?.length || 0}
        </p>
      </div>
      <div className="card">
        <p className="text-sm text-slate-500 dark:text-slate-400">Active Today</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">
          {laborSummaryData?.data?.daily?.length || 0}
        </p>
      </div>
      <div className="card">
        <p className="text-sm text-slate-500 dark:text-slate-400">Total Hours</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">
          {laborSummaryData?.data?.summary?.total_hours || 0}h
        </p>
      </div>
    </div>

    {/* Assigned Users List */}
    <div className="card">
      <h4 className="font-medium text-slate-900 dark:text-white mb-4">Assigned Users</h4>
      {(assignmentsData?.data || []).length === 0 ? (
        <div className="text-center py-8 text-slate-500 dark:text-slate-400">
          <UsersIcon className="h-12 w-12 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
          <p>No users assigned to this project yet</p>
          <button 
            onClick={() => setShowAssignModal(true)}
            className="btn-primary mt-4 inline-flex items-center gap-2"
          >
            <UserAddIcon className="h-4 w-4" />
            Assign First User
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {(assignmentsData?.data || []).map((assignment) => (
            <div key={assignment.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {assignment.full_name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{assignment.full_name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {assignment.role || 'General'} • {assignment.user_role}
                  </p>
                </div>
              </div>
              <span className={`badge ${assignment.role === 'project_manager' ? 'badge-primary' : 
                assignment.role === 'sub_manager' ? 'badge-info' :
                assignment.role === 'foreman' ? 'badge-warning' : 'badge-success'}`}>
                {assignment.role || 'Worker'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)}


        {activeTab === "timeline" && (
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Project Timeline
            </h3>
            {timelineLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-4">
                {(timelineData?.data || []).map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          item.status === "completed"
                            ? "bg-green-500"
                            : item.status === "in_progress"
                              ? "bg-yellow-500"
                              : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      />
                      {index < (timelineData?.data?.length || 0) - 1 && (
                        <div className="w-0.5 h-12 bg-slate-300 dark:bg-slate-600" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {item.name}
                        </span>
                        <span
                          className={`badge ${
                            item.status === "completed"
                              ? "badge-success"
                              : item.status === "in_progress"
                                ? "badge-warning"
                                : "badge-info"
                          }`}
                        >
                          {item.status || "Pending"}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {item.start_date
                          ? new Date(item.start_date).toLocaleDateString()
                          : "N/A"}{" "}
                        →
                        {item.end_date
                          ? new Date(item.end_date).toLocaleDateString()
                          : "N/A"}
                      </p>
                      {item.progress !== undefined && (
                        <div className="mt-1 w-32 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{ width: `${item.progress || 0}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "budget" && (
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Budget Overview
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Total Budget
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${project?.total_budget?.toLocaleString() || "0"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Current Spent
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${project?.current_spent?.toLocaleString() || "0"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Remaining
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    $
                    {(
                      project?.total_budget - project?.current_spent
                    )?.toLocaleString() || "0"}
                  </p>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    Budget Utilization
                  </span>
                  <span className="text-slate-900 dark:text-white">
                    {project?.total_budget
                      ? Math.round(
                          (project.current_spent / project.total_budget) * 100,
                        )
                      : 0}
                    %
                  </span>
                </div>
                <div className="mt-1 w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      project?.total_budget &&
                      project.current_spent / project.total_budget > 0.9
                        ? "bg-red-500"
                        : project.current_spent / project.total_budget > 0.7
                          ? "bg-yellow-500"
                          : "bg-gradient-to-r from-primary-500 to-primary-600"
                    }`}
                    style={{
                      width: `${
                        project?.total_budget
                          ? Math.min(
                              (project.current_spent / project.total_budget) *
                                100,
                              100,
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {showStageModal && (
          <StageForm
            projectId={parseInt(id)}
            onClose={() => setShowStageModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries(["project-stages", id]);
              setShowStageModal(false);
              toast.success("Stage created successfully");
            }}
          />
        )}
        {showTaskModal && (
          <TaskForm
            projectId={parseInt(id)}
            onClose={() => setShowTaskModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries(["project-tasks", id]);
              setShowTaskModal(false);
              toast.success("Task created successfully");
            }}
          />
        )}
        {showEditModal && (
          <ProjectForm
            project={project}
            onClose={() => setShowEditModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries(["project", id]);
              setShowEditModal(false);
              toast.success("Project updated successfully");
            }}
          />
        )}
        {/* Assign Worker Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Assign Worker to Project
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Select a worker to assign to this project
                  </p>
                </div>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <XIcon className="h-5 w-5 text-slate-500" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  assignMutation.mutate({
                    worker_id: parseInt(formData.get("worker_id")),
                    project_id: parseInt(id),
                    role: formData.get("role") || null,
                    notes: formData.get("notes") || null,
                  });
                }}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Select Worker *
                  </label>
                  <select
                    name="worker_id"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select a worker</option>
                    {(workersData?.data || []).map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {worker.full_name}{" "}
                        {worker.specialty ? `- ${worker.specialty}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Role
                  </label>
                  <input
                    type="text"
                    name="role"
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. Electrician, Plumber, Foreman"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    rows="2"
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="Additional notes"
                  />
                </div>
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={assignMutation.isLoading}
                    className="btn-primary"
                  >
                    {assignMutation.isLoading
                      ? "Assigning..."
                      : "Assign Worker"}
                  </button>
                </div>
              </form>

              {showAssignModal && (
                <AssignUserModal
                  projectId={parseInt(id)}
                  onClose={() => setShowAssignModal(false)}
                  onSuccess={() => {
                    queryClient.invalidateQueries(["project-assignments", id]);
                    setShowAssignModal(false);
                    toast.success("User assigned successfully");
                  }}
                />
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailPage;
